/**
 * Loan Detail Panel — slide-out panel with inline editing for loan entries.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { titleCaseCompanyName } from "@cvx/lib/companyName";
import { X, FileText, ChevronRight, Pencil, Check, Trash2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { cn } from "@/utils/misc";
import { usePersistedDisclosure } from "@/utils/use-persisted-disclosure";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import { Badge } from "@/ui/primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import type { Id } from "@cvx/_generated/dataModel";
import {
  deriveLoanBalanceEstimates,
  isExplicitRocapSourceType,
  resolveRocapValuationIndex,
} from "@cvx/jobs/summarization/lib/rocapValuation";
import {
  getValuationReliability,
  getValuationSourceIcon,
  normalizeSourceType,
} from "../assetList/constants";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "../shared-panels/ChangeHistory";
import { LOAN_FIELD_FORMATTERS, LOAN_FIELD_LABELS } from "./historyFormatters";
import { SlideOverPanel } from "../shared-panels/SlideOverPanel";
import { LinkedRecordsSection } from "../shared-panels/LinkedRecordsSection";
import { SuggestedLinksSection } from "../shared-panels/SuggestedLinksSection";
import {
  ConfirmEditDialog,
  type PendingEdit,
} from "../shared-panels/ConfirmEditDialog";
import type { RecordType } from "../shared-panels/linkShared";
import type { LoanEntryUI } from "./types";
import { DocumentDetail } from "../../documents/DocumentDetail";
import {
  directorDisclosureLabel,
  type DirectorDisclosureLabel,
} from "@/lib/director-disclosure";

/**
 * `updateLoanField` still declares `value: v.any()` since the mutation
 * accepts whatever type the target loan field holds. Every call site in
 * this panel only ever saves a text/date string, a numeric amount, or
 * `undefined` (to clear a field) — that's what's typed here.
 */
type FieldSaveValue = string | number | undefined;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtCurrency = (n?: number) =>
  n == null
    ? "\u2014"
    : n.toLocaleString("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 2,
      });
const fmtDate = (d?: string) => {
  if (!d) return "\u2014";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const DIRECTION_LABELS: Record<string, string> = {
  Loan_Granted: "Granted (money out)",
  Loan_Taken: "Taken (money in)",
};
const AGREEMENT_LABELS: Record<string, string> = {
  Formal_Contract: "Formal contract",
  Invoice_Only: "Invoice only",
  No_Documentation: "No documentation",
};
const SECURITY_LABELS: Record<string, string> = {
  Unsecured: "Unsecured",
  PPSR_Registered: "PPSR registered",
  Mortgage_Registered: "Mortgage registered",
};
const REPAYMENT_LABELS: Record<string, string> = {
  Interest_Only: "Interest only",
  Principal_And_Interest: "P&I",
  At_Call: "At call",
  Balloon: "Balloon",
};

// ---------------------------------------------------------------------------
// Editable field
// ---------------------------------------------------------------------------

function EditableField({
  label,
  value,
  displayValue,
  field,
  type = "text",
  onSave,
  isViewer,
}: {
  label: string;
  value?: string | number | null;
  displayValue?: string;
  field: string;
  type?: "text" | "date" | "number";
  onSave: (field: string, value: FieldSaveValue) => void;
  isViewer?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));

  const handleSave = () => {
    const parsed: FieldSaveValue =
      type === "number"
        ? draft
          ? parseFloat(draft)
          : undefined
        : draft || undefined;
    onSave(field, parsed);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center justify-between gap-2 py-2">
        <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1">
          <Input
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 w-36 text-sm"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
          <button
            onClick={handleSave}
            className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between gap-4 py-2">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="text-right text-sm text-foreground">
          {displayValue || String(value ?? "\u2014")}
        </span>
        {!isViewer && (
          <button
            onClick={() => {
              setDraft(String(value ?? ""));
              setEditing(true);
            }}
            className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  field,
  onSave,
  isViewer,
}: {
  label: string;
  value: string;
  options: Record<string, string>;
  field: string;
  onSave: (field: string, value: FieldSaveValue) => void;
  isViewer?: boolean;
}) {
  if (isViewer) {
    return (
      <div className="flex items-center justify-between gap-4 py-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="text-sm text-foreground">
          {options[value] ?? value}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={(v) => onSave(field, v)}>
        <SelectTrigger className="h-7 w-44 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(options).map(([k, v]) => (
            <SelectItem key={k} value={k}>
              {v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]">
      {children}
    </div>
  );
}

/**
 * Collapsible section: a `SectionHeader`-styled toggle button with a rotating
 * chevron that shows/hides its children. Defaults to expanded.
 */
function CollapsibleSection({
  title,
  defaultOpen = true,
  persistId,
  children,
}: {
  title: React.ReactNode;
  defaultOpen?: boolean;
  persistId?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = usePersistedDisclosure(persistId, defaultOpen);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]"
      >
        {title}
        <ChevronRight
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
      </button>
      {open && children}
    </>
  );
}

function EditableBannerField({
  label,
  value,
  field,
  onSave,
  isViewer,
}: {
  label: string;
  value: number;
  field: string;
  onSave: (field: string, value: FieldSaveValue) => void;
  isViewer?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));

  const handleSave = () => {
    const parsed = draft ? parseFloat(draft) : 0;
    onSave(field, parsed);
    setEditing(false);
  };

  if (editing) {
    return (
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {label}
        </div>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-8 w-full font-mono text-lg font-bold"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
          <button onClick={handleSave} className="p-1 text-emerald-600">
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="p-1 text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("group", !isViewer && "cursor-pointer")}
      onClick={() => {
        if (!isViewer) {
          setDraft(String(value));
          setEditing(true);
        }
      }}
    >
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-xl font-bold tabular-nums">
          {fmtCurrency(value)}
        </span>
        {!isViewer && (
          <Pencil className="h-3 w-3 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Balance sources (per-source figures, mirrors the Assets valuations UI)
// ---------------------------------------------------------------------------

type LoanBalanceEstimateUI = NonNullable<
  LoanEntryUI["balanceEstimates"]
>[number];

/** One per-source balance card — source icon, reliability tier, date, value. */
function BalanceEstimateCard({
  estimate,
  isRocap,
  disclosure,
}: {
  estimate: LoanBalanceEstimateUI;
  /** True when this estimate is the resolved ROCAP figure for the loan. */
  isRocap: boolean;
  /** Director-disclosure label for the appointment type (ROCAP / SOA / …). */
  disclosure: DirectorDisclosureLabel;
}) {
  const reliability = getValuationReliability(estimate.sourceType);
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold",
              reliability.iconClass,
            )}
          >
            {getValuationSourceIcon(estimate.sourceType)}
          </span>
          <div>
            <div className="text-sm font-medium text-foreground">
              {normalizeSourceType(estimate.sourceType) === "ROCAP"
                ? disclosure.short
                : normalizeSourceType(estimate.sourceType)}
              {estimate.directorName ? ` — ${estimate.directorName}` : ""}
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className={cn("h-1.5 w-1.5 rounded-full", reliability.dotClass)}
              />
              <span className={cn("text-xs", reliability.textClass)}>
                {reliability.label}
              </span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-sm font-bold tabular-nums">
            {fmtCurrency(estimate.value)}
          </div>
          {estimate.asAtDate && (
            <div className="text-xs text-muted-foreground">
              {fmtDate(estimate.asAtDate)}
            </div>
          )}
        </div>
      </div>
      {isRocap && (
        <div className="mt-2">
          <Badge color="indigo" variant="soft" size="sm">
            {isExplicitRocapSourceType(estimate.sourceType)
              ? `Director ${disclosure.short}`
              : `Derived from ${disclosure.full}`}
          </Badge>
        </div>
      )}
      {estimate.sourceDocument && (
        <div className="mt-2 truncate rounded-md border border-border bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground">
          Source: {estimate.sourceDocument.name}
          {estimate.sourceDocument.section
            ? ` · ${estimate.sourceDocument.section}`
            : ""}
        </div>
      )}
      {estimate.notes && (
        <p className="mt-2 text-xs text-muted-foreground">{estimate.notes}</p>
      )}
    </div>
  );
}

/**
 * Normalise agent-generated notes so `•` inline bullets render as separate lines.
 * Converts `• item` patterns into markdown list items (`- item`) on their own lines.
 */
function normaliseNotesForMarkdown(raw: string): string {
  return raw.replace(/\s*•\s*/g, "\n- ").trim();
}

function NotesSection({
  notes,
  onSave,
  isViewer,
}: {
  notes: string;
  onSave: (value: string) => void;
  isViewer?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(notes ?? "");

  const handleSave = () => {
    if (draft !== (notes ?? "")) onSave(draft);
    setEditing(false);
  };

  const normalisedNotes = notes ? normaliseNotesForMarkdown(notes) : "";

  return (
    <>
      <div className="flex items-center justify-between pb-1.5 pt-6">
        <div className="text-xs font-medium uppercase tracking-widest text-[#121212]">
          Notes
        </div>
        {!isViewer && !editing && (
          <button
            onClick={() => {
              setDraft(notes ?? "");
              setEditing(true);
            }}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add notes (supports **bold** and - bullet points)..."
            className="mt-1 min-h-[160px] resize-y p-3 text-sm leading-relaxed"
            autoFocus
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={handleSave}
              className="rounded bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Save
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div
            className={cn(
              "prose prose-sm relative max-w-none overflow-hidden py-2 transition-[max-height] duration-200 dark:prose-invert [&_li]:text-sm [&_li]:leading-relaxed [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-foreground [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-4",
              !expanded && "max-h-[180px]",
            )}
          >
            {normalisedNotes ? (
              <ReactMarkdown>{normalisedNotes}</ReactMarkdown>
            ) : (
              <p className="text-muted-foreground">—</p>
            )}
            {normalisedNotes && !expanded && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
            )}
          </div>
          {normalisedNotes && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 rounded-md border border-primary/30 px-3 py-1 text-xs font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface LoanDetailPanelProps {
  entry: LoanEntryUI | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
  isViewer?: boolean;
  placement?: "primary" | "secondary";
  /** Position in the secondary panel stack (1 = closest to primary). */
  stackIndex?: number;
  onOpenLinked?: (type: RecordType, id: string) => void;
  /**
   * Director-disclosure label for the appointment type (ROCAP / SOA / …).
   * Defaults to ROCAP when omitted (e.g. from generic linked-record stacks).
   */
  disclosure?: DirectorDisclosureLabel;
}

export function LoanDetailPanel({
  entry,
  open,
  onClose,
  onDeleted,
  isViewer,
  placement = "primary",
  stackIndex,
  onOpenLinked,
  disclosure = directorDisclosureLabel(undefined),
}: LoanDetailPanelProps) {
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  // Edit awaiting practitioner confirmation (all inline edits pass through
  // the shared ConfirmEditDialog before any mutation fires).
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);

  const updateField = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.loans.publicMutations.updateLoanField,
    ),
  });
  const deleteLoan = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.loans.publicMutations.deleteLoan,
    ),
  });

  useEffect(() => {
    if (open) setConfirmDelete(false);
  }, [open]);

  // Intentionally omits `editingName`: this should auto-enter edit mode
  // once per panel open for a freshly-created loan, not every time
  // `editingName` changes. Adding it as a dependency would re-fire this
  // effect the moment the practitioner cancels (Escape / Cancel button)
  // while the name is still "New Loan", immediately flipping editing back
  // on and trapping them in a loop they can't dismiss.
  useEffect(() => {
    if (open && entry?.counterpartyName === "New Loan" && !editingName) {
      setNameDraft("New Loan");
      setEditingName(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry?.counterpartyName]);

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    if (!entry?.changeHistory) return [];
    const ids = new Set<Id<"users">>();
    for (const change of entry.changeHistory) {
      if (change.userId) ids.add(change.userId);
    }
    return Array.from(ids);
  }, [entry?.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });

  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  if (!entry) return null;

  // Stages the edit behind the shared confirmation dialog — the mutation only
  // fires once the practitioner confirms (optionally leaving a note).
  const handleFieldSave = (field: string, value: FieldSaveValue) => {
    setPendingEdit({
      fieldLabel: LOAN_FIELD_LABELS[field] ?? field,
      onConfirm: (note) =>
        updateField.mutateAsync({
          entryId: entry._id as Id<"loanEntries">,
          field,
          value,
          note,
        }),
    });
  };

  const handleDelete = () => {
    deleteLoan.mutate(
      { entryId: entry._id as Id<"loanEntries"> },
      {
        onSuccess: () => {
          onDeleted?.();
          onClose();
        },
      },
    );
  };

  const dirConfig = {
    Loan_Granted: { color: "red" as const, label: "Granted" },
    Loan_Taken: { color: "emerald" as const, label: "Taken" },
  };
  const dir = dirConfig[entry.direction] ?? dirConfig.Loan_Taken;

  // The estimate driving the loan's ROCAP figure — an explicit ROCAP tag, an
  // agent-supplied figure sourced from a ROCAP / SoA annexure, or one derived
  // from the loan's own source documents when the agent didn't populate
  // balanceEstimates (same rule as Assets).
  const balanceEstimates = deriveLoanBalanceEstimates(entry);
  const rocapEstimateIndex = resolveRocapValuationIndex(
    balanceEstimates,
    undefined,
  );
  const rocapEstimate =
    rocapEstimateIndex != null
      ? (balanceEstimates[rocapEstimateIndex] ?? null)
      : null;

  return (
    <>
      <SlideOverPanel
        open={open}
        onClose={onClose}
        placement={placement}
        stackIndex={stackIndex}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b bg-background/95 px-5 pb-3 pt-5 backdrop-blur-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {!isViewer && editingName ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    className="h-8 text-lg font-semibold"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleFieldSave("counterpartyName", nameDraft);
                        setEditingName(false);
                      }
                      if (e.key === "Escape") setEditingName(false);
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      handleFieldSave("counterpartyName", nameDraft);
                      setEditingName(false);
                    }}
                    className="p-1 text-emerald-600"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="p-1 text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  className={cn("group", !isViewer && "cursor-pointer")}
                  onClick={() => {
                    if (!isViewer) {
                      setNameDraft(entry.counterpartyName);
                      setEditingName(true);
                    }
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <h2 className="mt-0.5 text-lg font-semibold leading-snug text-foreground">
                      {titleCaseCompanyName(entry.counterpartyName)}
                    </h2>
                    {!isViewer && (
                      <Pencil className="mt-1 h-3.5 w-3.5 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-0.5 flex shrink-0 items-center gap-1">
              {!isViewer && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
                  title="Delete loan"
                >
                  <Trash2 className="h-[16px] w-[16px]" />
                </button>
              )}
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Badge color={dir.color} variant="soft" size="sm">
              {dir.label}
            </Badge>
            {entry.isManualOverride && (
              <Badge color="emerald" variant="soft" size="sm">
                Manually edited
              </Badge>
            )}
          </div>
        </div>

        {/* Delete confirmation */}
        {confirmDelete && (
          <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
            <p className="text-sm text-red-700 dark:text-red-300">
              Remove this loan from the register?
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteLoan.isPending}
              >
                {deleteLoan.isPending ? "Removing..." : "Remove"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="px-5 pb-8">
          {/* Amount banner — editable */}
          <div className="mt-4 rounded-lg border bg-muted/30 p-4">
            <div className="grid grid-cols-2 gap-4">
              <EditableBannerField
                label="Original amount"
                value={entry.amount}
                field="amount"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
              <EditableBannerField
                label="Outstanding balance"
                value={entry.outstandingBalance}
                field="outstandingBalance"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
            </div>
          </div>

          {/* Balance sources — per-source figures incl. the ROCAP figure */}
          {balanceEstimates.length > 0 && (
            <>
              <CollapsibleSection
                title={`Balance sources (${balanceEstimates.length})`}
                persistId="loan-detail:balance-sources"
              >
                <div className="flex flex-col gap-2 pt-1">
                  <div className="flex items-center gap-2 pb-1">
                    <span className="text-xs text-muted-foreground">
                      {disclosure.short}
                    </span>
                    {rocapEstimate ? (
                      <>
                        <span className="font-mono text-xs font-semibold tabular-nums">
                          {fmtCurrency(rocapEstimate.value)}
                        </span>
                        <Badge color="indigo" variant="soft" size="sm">
                          {isExplicitRocapSourceType(rocapEstimate.sourceType)
                            ? `Director ${disclosure.short}`
                            : `Derived from ${disclosure.full}`}
                        </Badge>
                      </>
                    ) : (
                      <Badge color="red" variant="soft" size="sm">
                        No {disclosure.short} figure
                      </Badge>
                    )}
                  </div>
                  {balanceEstimates.map((estimate, i) => (
                    <BalanceEstimateCard
                      key={i}
                      estimate={estimate}
                      isRocap={i === rocapEstimateIndex}
                      disclosure={disclosure}
                    />
                  ))}
                </div>
              </CollapsibleSection>
              <div className="mt-4 border-b" />
            </>
          )}

          {/* Loan details */}
          <SectionHeader>Loan details</SectionHeader>
          <EditableField
            label="Counterparty"
            value={entry.counterpartyName}
            field="counterpartyName"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Direction"
            value={entry.direction}
            options={DIRECTION_LABELS}
            field="direction"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Agreement"
            value={entry.agreementStatus}
            options={AGREEMENT_LABELS}
            field="agreementStatus"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Security"
            value={entry.securityStatus}
            options={SECURITY_LABELS}
            field="securityStatus"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Repayment"
            value={entry.repaymentTerms}
            options={REPAYMENT_LABELS}
            field="repaymentTerms"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="Interest rate"
            value={entry.interestRateAnnual}
            displayValue={
              entry.interestRateAnnual != null
                ? `${entry.interestRateAnnual}% p.a.`
                : undefined
            }
            field="interestRateAnnual"
            type="number"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="Date"
            value={entry.date}
            displayValue={fmtDate(entry.date)}
            field="date"
            type="date"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <div className="mt-2 border-b" />

          {/* Notes */}
          <NotesSection
            notes={entry.notes}
            onSave={(val) => handleFieldSave("notes", val)}
            isViewer={isViewer}
          />
          <div className="mt-4 border-b" />

          {/* Source documents */}
          {entry.sourceDocuments.length > 0 && (
            <>
              <CollapsibleSection
                title="Source documents"
                persistId="loan-detail:source-documents"
              >
                <div className="flex flex-col gap-2 pt-2.5">
                  {entry.sourceDocuments.map((doc, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        setViewingDocumentId(doc.documentId as Id<"documents">)
                      }
                      className="group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {doc.fileName}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </CollapsibleSection>
              <div className="mt-4 border-b" />
            </>
          )}

          {/* Linked records */}
          {onOpenLinked && (
            <div className="mt-4 space-y-4">
              <SuggestedLinksSection
                jobId={entry.jobId}
                anchor={{ type: "loan", id: entry._id }}
                onOpenLinked={onOpenLinked}
                isViewer={isViewer}
              />
              <LinkedRecordsSection
                jobId={entry.jobId}
                anchor={{ type: "loan", id: entry._id }}
                onOpenLinked={onOpenLinked}
                isViewer={isViewer}
              />
            </div>
          )}

          {/* History */}
          {entry.changeHistory && (
            <div className="mt-4">
              <ChangeHistory
                history={entry.changeHistory}
                usersById={historyUsersById}
                formatters={LOAN_FIELD_FORMATTERS}
                fieldLabels={LOAN_FIELD_LABELS}
                persistId="loan-detail:history"
              />
            </div>
          )}
        </div>
      </SlideOverPanel>

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />

      {/* Confirmation gate for every inline edit in this panel */}
      <ConfirmEditDialog
        edit={pendingEdit}
        onClose={() => setPendingEdit(null)}
      />
    </>
  );
}
